import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useConsultation } from '../contexts/ConsultationContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Loader2, Sparkles, Plus, RefreshCw, FileText, Pill, TestTube, Save, Search, X, ChevronDown, ChevronUp, Package, History } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format Mon DD label e.g. "20 Apr". Used for the "From Last Visit" header
// chip — short and IST-agnostic since the date is informational.
function shortDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' });
  } catch (_) { return ''; }
}

export function AISuggestionsPanel({ activeSection, onClose, priorPrescription = [], priorSource = null }) {
  const { getSuggestions, consultation } = useConsultation();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [prescriptionTemplates, setPrescriptionTemplates] = useState([]);
  const [investigationTemplates, setInvestigationTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dbSearchResults, setDbSearchResults] = useState([]);
  const [dbSearchTimeout, setDbSearchTimeout] = useState(null);
  // "From Last Visit" defaults to expanded — it's the most-relevant
  // recommendation when the patient is a returning visitor.
  const [expandedSections, setExpandedSections] = useState({ priorRx: true, medicines: true, investigations: true, templates: false, ai: false });

  const getCurrentDiagnosis = () => {
    const provisional = consultation?.sections?.provisional_diagnosis?.content || '';
    const final_diag = consultation?.sections?.final_diagnosis?.content || '';
    const diagnosis = final_diag || provisional;
    return diagnosis.replace(/<[^>]*>/g, ' ').trim().toLowerCase();
  };

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const diagnosis = getCurrentDiagnosis();
        const params = diagnosis ? { diagnosis } : {};
        const { data } = await axios.get(`${API_URL}/api/templates`, { withCredentials: true, params });
        setPrescriptionTemplates(data.filter(t => t.content?.prescription?.length > 0));
        setInvestigationTemplates(data.filter(t => t.content?.investigations?.length > 0));
      } catch (e) { /* ignore */ }
    };
    loadTemplates();
  }, [consultation?.sections?.provisional_diagnosis?.content, consultation?.sections?.final_diagnosis?.content]);

  const getContext = useCallback(() => {
    if (!consultation?.sections) return '';
    const complaints = consultation.sections.chief_complaints?.content || '';
    const history = consultation.sections.past_history?.content || '';
    const examination = consultation.sections.clinical_examination?.content || '';
    const diagnosis = consultation.sections.final_diagnosis?.content || consultation.sections.provisional_diagnosis?.content || '';
    const stripHtml = (html) => html.replace(/<[^>]*>/g, ' ').trim();
    return [
      complaints && `Chief Complaints: ${stripHtml(complaints)}`,
      diagnosis && `Diagnosis: ${stripHtml(diagnosis)}`,
      history && `History: ${stripHtml(history)}`,
      examination && `Examination: ${stripHtml(examination)}`
    ].filter(Boolean).join('\n');
  }, [consultation]);

  const fetchSuggestions = async (type) => {
    const context = getContext();
    if (!context) {
      setSuggestions([{ error: 'Add chief complaints or diagnosis first' }]);
      return;
    }
    setLoading(true);
    setExpandedSections(prev => ({ ...prev, ai: true }));
    const result = await getSuggestions(context, type || 'medicine');
    if (result.success) setSuggestions(result.suggestions || []);
    else setSuggestions([{ error: result.error || 'Failed to get suggestions' }]);
    setLoading(false);
  };

  const addMedicine = (med) => {
    if (window.addMedicineFromSuggestion) window.addMedicineFromSuggestion(med);
  };

  // Add a complete prior-visit medicine — preserves dosage/frequency/intake/
  // timing/duration/quantity/remarks so the doctor doesn't have to re-key
  // a chronic / continuing prescription.
  const addPriorMedicine = (m) => {
    const fullMed = {
      name: m.medicine_name,
      medicine_name: m.medicine_name,
      dosage: m.dosage || '',
      frequency: m.frequency || '',
      intake: m.intake || '',
      duration: m.duration || '',
      quantity: m.quantity || '',
      timing: m.timing || [],
      timing_doses: m.timing_doses || {},
      remarks: m.remarks || '',
      composition: m.composition || '',
    };
    if (window.addMedicineFromSuggestion) window.addMedicineFromSuggestion(fullMed);
  };

  // De-dupe meds already added to current consultation so the carry-forward
  // list only highlights what's still actionable.
  const currentRxNames = new Set(
    ((consultation?.sections?.prescription?.items) || [])
      .map(m => (m.medicine_name || m.name || '').trim().toLowerCase())
      .filter(Boolean)
  );
  const visiblePriorRx = (priorPrescription || []).filter(m =>
    !currentRxNames.has((m.medicine_name || '').trim().toLowerCase())
  );

  const applyMedicineTemplate = (tpl) => {
    const meds = tpl?.content?.prescription || [];
    if (typeof window.addManyMedicinesFromSuggestion === 'function') {
      window.addManyMedicinesFromSuggestion(meds);
      toast.success(`Applied ${meds.length} medicines from "${tpl.name}"`);
    } else if (typeof window.addMedicineFromSuggestion === 'function') {
      // Fallback for older builds — sequential adds (still works because the
      // section now uses functional setState, but the bulk helper is faster).
      meds.forEach(med => addMedicine(med));
      toast.success(`Applied ${meds.length} medicines from "${tpl.name}"`);
    } else {
      // Section not mounted yet — guide the user instead of pretending.
      toast.error('Open the Prescription section first, then click the template again.');
    }
  };

  const applyInvestigationTemplate = (tpl) => {
    const tests = tpl?.content?.investigations || [];
    if (typeof window.addManyInvestigationsFromSuggestion === 'function') {
      window.addManyInvestigationsFromSuggestion(tests);
      toast.success(`Applied ${tests.length} tests from "${tpl.name}"`);
    } else if (typeof window.addInvestigationFromSuggestion === 'function') {
      tests.forEach(test => addInvestigation(test));
      toast.success(`Applied ${tests.length} tests from "${tpl.name}"`);
    } else {
      toast.error('Open the Investigations section first, then click the template again.');
    }
  };

  const addInvestigation = (test) => {
    if (window.addInvestigationFromSuggestion) window.addInvestigationFromSuggestion(test);
  };

  // Add medicine from DB search with low stock/out-of-stock handling
  const addMedicineFromDbSearch = (result) => {
    const med = { name: result.name, dosage: '', frequency: 'BD', duration: '5 days' };
    if (window.addMedicineFromSuggestion) window.addMedicineFromSuggestion(med);
    if (result.stock != null && result.stock <= 10) {
      const isOutOfStock = result.stock <= 0;
      toast.warning(
        <div>
          <p className="font-semibold">{isOutOfStock ? 'Stock not available in Pharmacy' : 'Low stock in Pharmacy'}</p>
          <p className="text-xs mt-1">{result.name}{!isOutOfStock ? ` (${Math.floor(result.stock)} remaining)` : ''}</p>
          <p className="text-xs text-gray-500 mt-1">Purchase notification will be sent for immediate purchase.</p>
        </div>,
        { duration: 5000 }
      );
      axios.post(`${API_URL}/api/purchase-requests`, {
        medicine_name: result.name,
        composition: result.composition || '',
        patient_name: consultation?.patient_name || '',
        patient_id: consultation?.patient_id || '',
        quantity: 1,
      }, { withCredentials: true }).catch(() => {});
    }
    setSearchQuery('');
    setDbSearchResults([]);
  };

  const [searchLoading, setSearchLoading] = useState(false);
  const searchAbortRef = useRef(null);
  const searchReqIdRef = useRef(0);

  // DB search for medicines and investigations
  const handleSidebarSearch = (query) => {
    setSearchQuery(query);
    if (dbSearchTimeout) clearTimeout(dbSearchTimeout);
    if (!query || query.length < 2) {
      setDbSearchResults([]);
      setSearchLoading(false);
      // Cancel any in-flight request so its (now-irrelevant) response
      // doesn't repopulate results.
      if (searchAbortRef.current) searchAbortRef.current.abort();
      return;
    }
    setSearchLoading(true);
    const timeout = setTimeout(async () => {
      // Guard against stale responses overwriting newer queries.
      if (searchAbortRef.current) searchAbortRef.current.abort();
      const controller = new AbortController();
      searchAbortRef.current = controller;
      const reqId = ++searchReqIdRef.current;
      try {
        const [medRes, testRes] = await Promise.all([
          axios.get(`${API_URL}/api/common/medicines`, { params: { search: query }, withCredentials: true, signal: controller.signal }),
          axios.get(`${API_URL}/api/common/tests`, { params: { search: query }, withCredentials: true, signal: controller.signal }),
        ]);
        if (reqId !== searchReqIdRef.current) return; // stale, ignore
        setDbSearchResults([
          ...(medRes.data || []).slice(0, 10).map(m => ({ ...m, type: 'medicine' })),
          ...(testRes.data || []).slice(0, 5).map(t => ({ ...t, type: 'investigation' })),
        ]);
      } catch (e) {
        if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
        if (reqId !== searchReqIdRef.current) return;
        // On real failures: clear stale results so the doctor doesn't see
        // misleading hits from a previous query.
        setDbSearchResults([]);
      } finally {
        if (reqId === searchReqIdRef.current) setSearchLoading(false);
      }
    }, 300);
    setDbSearchTimeout(timeout);
  };

  // Quick-add lists — loaded from backend `/api/quick-adds` which is gated to
  // items currently in Inventory (stock > 0, non-expired) and tests that exist
  // in CommonMaster.investigationmasters. NEVER hardcode — doctors must only
  // see orderable items.
  const [commonMedicines, setCommonMedicines] = useState([]);
  const [commonInvestigations, setCommonInvestigations] = useState([]);

  useEffect(() => {
    const chief = consultation?.sections?.chief_complaints?.content || '';
    const symptoms = chief.replace(/<[^>]*>/g, ' ').trim();
    const params = symptoms ? { symptoms } : {};
    const controller = new AbortController();
    axios.get(`${API_URL}/api/quick-adds`, { params, withCredentials: true, signal: controller.signal })
      .then(({ data }) => {
        // Normalize medicine entries to { name, dosage, frequency, duration }
        const meds = (data.medicines || []).map(m => ({
          name: m.medicine_name || m.name || '',
          dosage: m.dosage || '',
          frequency: m.frequency || '',
          duration: m.duration || '',
        })).filter(m => m.name);
        setCommonMedicines(meds);
        // Normalize investigation entries to strings
        const invs = (data.investigations || []).map(i =>
          typeof i === 'string' ? i : (i?.name || '')
        ).filter(Boolean);
        setCommonInvestigations(invs);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [consultation?.sections?.chief_complaints?.content]);

  const q = searchQuery.toLowerCase();
  const filteredMeds = (q && !dbSearchResults.length) ? commonMedicines.filter(m => m.name.toLowerCase().includes(q)) : commonMedicines;
  const filteredInv = (q && !dbSearchResults.length) ? commonInvestigations.filter(i => i.toLowerCase().includes(q)) : commonInvestigations;

  const toggle = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <aside className="w-80 border-l border-gray-200 bg-white flex flex-col h-full relative" data-testid="ai-suggestions-panel">
      {onClose && (
        <button onClick={onClose} className="lg:hidden absolute top-2 right-2 z-10 p-1 rounded hover:bg-gray-200" data-testid="close-ai-panel">
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Header */}
      <div className="p-3 border-b border-gray-200 flex-shrink-0 bg-gradient-to-r from-blue-50 to-white">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-blue-700" />
          <h3 className="font-bold text-sm text-gray-800" style={{ fontFamily: 'Manrope' }}>Clinical Assistant</h3>
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input value={searchQuery} onChange={(e) => handleSidebarSearch(e.target.value)}
            placeholder="Search medicines, tests..." className="pl-7 h-8 text-xs" data-testid="assistant-search" />
          {searchQuery && (
            <button onClick={() => { setSearchQuery(''); setDbSearchResults([]); }}
              className="absolute right-2 top-1/2 -translate-y-1/2">
              <X className="h-3 w-3 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2 pb-20">
          {/* DB Search Results */}
          {dbSearchResults.length > 0 && searchQuery && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/30 overflow-hidden" data-testid="sidebar-search-results">
              <div className="px-3 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
                <Search className="h-3.5 w-3.5 text-blue-700" />
                <span className="text-xs font-semibold text-blue-800">Search Results</span>
                <Badge variant="secondary" className="text-[9px] px-1.5 py-0">{dbSearchResults.length}</Badge>
              </div>
              <div className="divide-y divide-gray-100">
                {dbSearchResults.map((result, i) => (
                  <button key={i}
                    onClick={() => result.type === 'medicine' ? addMedicineFromDbSearch(result) : addInvestigation(result.name)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-blue-50 text-left transition-colors group"
                    data-testid={`sidebar-result-${i}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {result.type === 'medicine' ? <Pill className="h-3 w-3 text-blue-600 flex-shrink-0" /> : <TestTube className="h-3 w-3 text-purple-600 flex-shrink-0" />}
                        <p className="font-medium text-xs text-gray-800 truncate">{result.name}</p>
                      </div>
                      {result.composition && <p className="text-[10px] text-blue-600 truncate ml-4.5 pl-[18px]" title={result.composition}>{result.composition}</p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                      {result.type === 'medicine' && (
                        <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0 rounded ${
                          result.stock > 10 ? 'bg-green-50 text-green-700' :
                          result.stock > 0 ? 'bg-amber-50 text-amber-700' :
                          'bg-red-50 text-red-700'
                        }`}>
                          <Package className="h-2 w-2" />
                          {result.stock <= 0 ? 'No stock' : `${Math.floor(result.stock)} ${result.unit || ''}`}
                        </span>
                      )}
                      <Plus className="h-3.5 w-3.5 text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* From Last Visit — carry-forward for returning patients */}
          {visiblePriorRx.length > 0 && (
            <CollapsibleSection
              title="From Last Visit"
              icon={<History className="h-3.5 w-3.5 text-violet-700" />}
              expanded={expandedSections.priorRx}
              onToggle={() => toggle('priorRx')}
              count={visiblePriorRx.length}
              color="violet"
              testId="prior-visit-medicines"
              extraHeader={priorSource && (
                <span className="text-[10px] font-medium text-violet-700 truncate" title={`${priorSource.doctor_name || ''} · ${shortDate(priorSource.visit_date)}`}>
                  {priorSource.doctor_name ? `Dr. ${priorSource.doctor_name.split(' ')[0]}` : ''}
                  {priorSource.visit_date ? ` · ${shortDate(priorSource.visit_date)}` : ''}
                </span>
              )}
            >
              <div className="space-y-0.5">
                {visiblePriorRx.map((m, i) => {
                  const meta = [m.dosage, m.frequency, m.duration].filter(Boolean).join(' · ');
                  return (
                    <button
                      key={`prior-${i}`}
                      onClick={() => addPriorMedicine(m)}
                      className="w-full flex items-center justify-between p-1.5 rounded hover:bg-violet-50 text-left transition-colors group border border-transparent hover:border-violet-200"
                      data-testid={`prior-medicine-${i}`}
                      title="Add this medicine with the previous visit's dose, frequency and duration"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-xs text-gray-800 truncate">{m.medicine_name}</p>
                        {meta && <p className="text-[10px] text-violet-700 truncate">{meta}</p>}
                      </div>
                      <Plus className="h-3.5 w-3.5 text-violet-700 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  );
                })}
              </div>
            </CollapsibleSection>
          )}

          {/* Quick Add Medicines */}
          <CollapsibleSection title="Quick Add Medicines" icon={<Pill className="h-3.5 w-3.5 text-blue-700" />}
            expanded={expandedSections.medicines} onToggle={() => toggle('medicines')}
            count={filteredMeds.length} color="blue" testId="quick-medicines">
            <div className="space-y-0.5">
              {filteredMeds.map((med, i) => (
                <button key={i} onClick={() => addMedicine(med)}
                  className="w-full flex items-center justify-between p-1.5 rounded hover:bg-blue-50 text-left transition-colors group"
                  data-testid={`quick-medicine-${i}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-xs text-gray-800">{med.name}</p>
                  </div>
                  <Plus className="h-3.5 w-3.5 text-blue-700 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          </CollapsibleSection>

          {/* Quick Add Investigations */}
          <CollapsibleSection title="Quick Add Investigations" icon={<TestTube className="h-3.5 w-3.5 text-blue-600" />}
            expanded={expandedSections.investigations} onToggle={() => toggle('investigations')}
            count={filteredInv.length} color="blue" testId="quick-investigations">
            <div className="space-y-0.5">
              {filteredInv.map((test, i) => (
                <button key={i} onClick={() => addInvestigation(test)}
                  className="w-full flex items-center justify-between p-1.5 rounded hover:bg-blue-50 text-left transition-colors group"
                  data-testid={`quick-investigation-${i}`}>
                  <p className="font-medium text-xs text-gray-800">{test}</p>
                  <Plus className="h-3.5 w-3.5 text-blue-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          </CollapsibleSection>

          {/* Saved Templates */}
          {(prescriptionTemplates.length > 0 || investigationTemplates.length > 0) && (
            <CollapsibleSection title="Saved Templates" icon={<FileText className="h-3.5 w-3.5 text-purple-600" />}
              expanded={expandedSections.templates} onToggle={() => toggle('templates')}
              count={prescriptionTemplates.length + investigationTemplates.length} color="purple" testId="saved-templates">
              <div className="space-y-1.5">
                {prescriptionTemplates.map((tpl, i) => (
                  <button key={`rx-${i}`} onClick={() => applyMedicineTemplate(tpl)}
                    className="w-full p-2 rounded border border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-left transition-colors"
                    data-testid={`prescription-template-${i}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-xs">{tpl.name}</p>
                      <Badge className="bg-blue-100 text-blue-700 text-[9px] border-0">{tpl.content?.prescription?.length} meds</Badge>
                    </div>
                  </button>
                ))}
                {investigationTemplates.map((tpl, i) => (
                  <button key={`inv-${i}`} onClick={() => applyInvestigationTemplate(tpl)}
                    className="w-full p-2 rounded border border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-left transition-colors"
                    data-testid={`investigation-template-${i}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-xs">{tpl.name}</p>
                      <Badge className="bg-blue-100 text-blue-700 text-[9px] border-0">{tpl.content?.investigations?.length} tests</Badge>
                    </div>
                  </button>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* AI Suggestions */}
          <CollapsibleSection title="AI Suggestions" icon={<Sparkles className="h-3.5 w-3.5 text-amber-600" />}
            expanded={expandedSections.ai} onToggle={() => toggle('ai')}
            count={suggestions.length} color="amber" testId="ai-suggestions">
            {suggestions.length === 0 && !loading && (
              <div className="text-center py-3 space-y-2">
                <p className="text-[11px] text-gray-500">AI-powered suggestions based on patient data</p>
                <div className="flex gap-1.5 justify-center">
                  <Button size="sm" variant="outline" onClick={() => fetchSuggestions('medicine')}
                    className="h-7 text-[11px] gap-1" data-testid="ai-suggest-rx">
                    <Pill className="h-3 w-3" /> Medicines
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => fetchSuggestions('test')}
                    className="h-7 text-[11px] gap-1" data-testid="ai-suggest-inv">
                    <TestTube className="h-3 w-3" /> Tests
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => fetchSuggestions('diagnosis')}
                    className="h-7 text-[11px] gap-1" data-testid="ai-suggest-dx">
                    <Sparkles className="h-3 w-3" /> Dx
                  </Button>
                </div>
              </div>
            )}
            {loading && (
              <div className="flex items-center justify-center py-4 text-gray-500 gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <p className="text-xs">Analyzing patient data...</p>
              </div>
            )}
            {suggestions.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-gray-400">{suggestions.length} suggestions</span>
                  <Button variant="ghost" size="sm" onClick={() => fetchSuggestions('medicine')} className="h-5 w-5 p-0">
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                </div>
                {suggestions.map((s, i) => {
                  if (s.error) return <div key={i} className="text-xs text-amber-600 bg-amber-50 p-2 rounded">{s.error}</div>;
                  return (
                    <button key={i} onClick={() => s.dosage ? addMedicine(s) : addInvestigation(s.name || s)}
                      className={`w-full p-2 rounded border text-left transition-colors ${
                        s.red_flag ? 'border-red-300 bg-red-50 hover:bg-red-100'
                                   : s.interaction_warning ? 'border-amber-300 bg-amber-50 hover:bg-amber-100'
                                   : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                      data-testid={`ai-suggestion-${i}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-semibold text-xs text-zinc-900">{s.name || s.description || s}</p>
                            {s.red_flag && (
                              <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded bg-red-600 text-white">Red flag</span>
                            )}
                            {s.confidence && (
                              <span className={`text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded ${
                                s.confidence === 'high' ? 'bg-emerald-100 text-emerald-700'
                                : s.confidence === 'medium' ? 'bg-blue-100 text-blue-700'
                                : 'bg-zinc-100 text-zinc-600'
                              }`}>{s.confidence}</span>
                            )}
                            {s.priority && (
                              <span className={`text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded ${
                                s.priority === 'urgent' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                              }`}>{s.priority}</span>
                            )}
                          </div>
                          {s.dosage && <p className="text-[10px] text-zinc-500 mt-0.5">{s.dosage} &middot; {s.frequency} &middot; {s.duration}</p>}
                          {s.reason && <p className="text-[10px] text-zinc-500 mt-0.5">{s.reason}</p>}
                          {s.rationale && (
                            <p className="text-[10px] text-blue-700 italic mt-1 leading-snug">{s.rationale}</p>
                          )}
                          {s.dose_note && (
                            <p className="text-[10px] text-purple-700 font-semibold mt-0.5"><span className="opacity-70">Dose: </span>{s.dose_note}</p>
                          )}
                          {s.interaction_warning && (
                            <p className="text-[10px] text-amber-800 font-semibold mt-0.5 flex items-start gap-1"><span>⚠</span><span>{s.interaction_warning}</span></p>
                          )}
                          {s.grouped_under && (
                            <p className="text-[9px] text-zinc-400 mt-0.5">Group: {s.grouped_under}</p>
                          )}
                          {s.guideline && (
                            <p className="text-[9px] text-emerald-700 mt-0.5">📖 {s.guideline}</p>
                          )}
                          {s.code && <Badge variant="outline" className="text-[9px] mt-1">{s.code}</Badge>}
                        </div>
                        <Plus className="h-3.5 w-3.5 text-blue-700 flex-shrink-0 mt-0.5" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CollapsibleSection>
        </div>
      </ScrollArea>
    </aside>
  );
}

function CollapsibleSection({ title, icon, expanded, onToggle, count, color, children, testId, extraHeader }) {
  // Map → static class strings so Tailwind picks them up at build time.
  // Without this, dynamic `bg-${color}-50` strings get tree-shaken away.
  const palette = {
    blue: { headerOpen: 'bg-blue-50', headerHover: 'hover:bg-blue-50/60' },
    amber: { headerOpen: 'bg-amber-50', headerHover: 'hover:bg-amber-50/60' },
    violet: { headerOpen: 'bg-violet-50', headerHover: 'hover:bg-violet-50/60' },
  };
  const c = palette[color] || palette.blue;
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden" data-testid={testId}>
      <button onClick={onToggle}
        className={`w-full flex items-center justify-between px-3 py-2 ${expanded ? c.headerOpen : 'bg-gray-50'} ${c.headerHover} transition-colors`}>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {icon}
          <span className="text-xs font-semibold text-gray-700">{title}</span>
          {count > 0 && <Badge variant="secondary" className="text-[9px] px-1.5 py-0">{count}</Badge>}
          {extraHeader && <span className="ml-1 min-w-0 truncate">{extraHeader}</span>}
        </div>
        {expanded ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
      </button>
      {expanded && <div className="px-3 py-2 border-t border-gray-100">{children}</div>}
    </div>
  );
}
